import { createI18n } from "@kovernow/flows/i18n";
import { createFirebaseIdentity } from "@kovernow/flows/auth/firebase";
import { restoreSession } from "@kovernow/flows/auth";
import { applyTheme } from "@kovernow/react-ui/theme/apply-theme";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import { Providers, App } from "./App";
import { renderFatal } from "./bootstrap/fatal";
import { REGION_CHOICE_KEY, resolveConfig } from "./bootstrap/resolve-config";
import { initSentry } from "./core/sentry";
import { createServices } from "./core/services";
import { captureFromLocation, createSingpassReturnStore } from "./core/singpass-return";
import { buildRoutes } from "./router";
import { MaintenanceView } from "./screens/MaintenanceView";
import { RegionPicker } from "./screens/RegionPicker";
import "./index.css";

// Vite build-mode flag (not a tenant value): dev relaxes host checks.
// eslint-disable-next-line turbo/no-undeclared-env-vars
const isDev = import.meta.env.DEV;

function readRememberedRegion(): string | null {
  try {
    return window.localStorage.getItem(REGION_CHOICE_KEY);
  } catch {
    return null;
  }
}

async function fetchRuntimeConfig(): Promise<unknown> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

async function bootstrap() {
  // W-7: read and strip a SingPass return token before anything else
  // (Sentry, session restore, the auth guard) can see or copy the URL.
  const singpassReturn = createSingpassReturnStore();
  captureFromLocation(singpassReturn);
  const rootEl = document.getElementById("root")!;
  const resolution = resolveConfig({
    runtime: await fetchRuntimeConfig(),
    hostname: window.location.hostname,
    path: window.location.pathname + window.location.search + window.location.hash,
    rememberedRegion: readRememberedRegion(),
    allowLocalhost: isDev,
  });

  if (resolution.kind === "error") return renderFatal(rootEl, resolution.reason);
  if (resolution.kind === "redirect") return window.location.replace(resolution.url);

  const { config } = resolution;
  try {
    // A theme failing the contrast gate is rejected at load in development.
    applyTheme(config.theme, { enforceContrast: isDev });
  } catch (err) {
    return renderFatal(rootEl, (err as Error).message);
  }
  document.documentElement.lang = config.locale;
  const icon = document.createElement("link");
  icon.rel = "icon";
  icon.href = config.theme.logo.src;
  document.head.appendChild(icon);
  initSentry(config);

  const i18n = await createI18n({
    locale: config.locale,
    overrides: config.i18nOverrides,
    onMissingKey: isDev ? (key) => console.error(`[i18n] missing key: ${key}`) : undefined,
  });
  const identity = createFirebaseIdentity(config.firebase);
  const services = createServices({ config, i18n, identity, singpassReturn });
  const root = createRoot(rootEl);

  if (resolution.kind === "picker") {
    root.render(
      <StrictMode>
        <Providers services={services}>
          <RegionPicker regions={resolution.regions} path={window.location.pathname + window.location.search} />
        </Providers>
      </StrictMode>,
    );
    return;
  }

  const startApp = async () => {
    // Page load: restore the session from the refresh cookie before routing.
    await restoreSession(services.api, services.session);
    const router = createBrowserRouter(buildRoutes(services.session, config.region, services.singpassReturn));
    root.render(
      <StrictMode>
        <App services={services} router={router} />
      </StrictMode>,
    );
  };

  const status = await services.authApi.serverStatus().catch(() => "ok" as const);
  if (status === "maintenance") {
    root.render(
      <StrictMode>
        <Providers services={services}>
          <MaintenanceView onRecovered={() => void startApp()} />
        </Providers>
      </StrictMode>,
    );
    return;
  }
  await startApp();
}

void bootstrap();
